import { useMemo, useState } from "react";
import { Plus, Edit2, Trash2, Percent } from "lucide-react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Modal from "../ui/Modal";
import { Table, THead, TR, TH, TD } from "../ui/Table";
import { toISO, today } from "../../lib/dateUtils";

// Inline editor for a team member's partner rules. Each rule = one revenue or
// profit cut at a defined scope (department / offer / agency).
//
// Usage:
//   <PartnerRulesEditor
//     member={member}
//     state={state}
//     dashboard={dashboard}
//   />
//
// State writes go through dashboard.addPartnerRule / updatePartnerRule /
// deletePartnerRule (which optimistically update React state + Supabase).
export default function PartnerRulesEditor({ member, state, dashboard }) {
  const partnerRules = state.partnerRules || [];
  const offers = state.offers || [];
  const departments = state.settings?.departments || [];

  const rules = useMemo(
    () => partnerRules.filter((r) => r.partnerId === member.id),
    [partnerRules, member.id]
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const offerName = (id) => offers.find((o) => o.id === id)?.name || id;
  const scopeLabel = (rule) => {
    if (rule.scope === "agency") return "Agency total";
    if (rule.scope === "department") return `Department: ${rule.scopeId || "—"}`;
    if (rule.scope === "offer") return `Offer: ${offerName(rule.scopeId)}`;
    return rule.scope;
  };
  const basisLabel = (rule) => (rule.basis === "net_profit" ? "% of net profit" : "% of revenue");

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Percent size={14} className="text-purple-600" />
          <h3 className="text-sm font-display font-semibold text-brand-ink dark:text-[var(--text-primary)]">
            Partner rules
          </h3>
          <span className="text-[11px] text-brand-slate">
            {rules.length === 0 ? "none yet" : `${rules.length} active`}
          </span>
        </div>
        <Button size="sm" variant="secondary" onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus size={12} /> Add rule
        </Button>
      </div>

      {rules.length === 0 ? (
        <div className="text-xs text-brand-slate p-3 rounded-lg border border-dashed border-gray-200 dark:border-[var(--border-soft)]">
          No partner rules yet. Add one to track a % cut owed to {member.name} on top of their salary / payments. E.g. "50% of SEO revenue" or "25% of agency net profit".
        </div>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Scope</TH>
              <TH>Basis</TH>
              <TH className="text-right">%</TH>
              <TH>Window</TH>
              <TH></TH>
            </TR>
          </THead>
          <tbody>
            {rules.map((r) => (
              <TR key={r.id}>
                <TD className="text-xs font-medium">{scopeLabel(r)}</TD>
                <TD className="text-xs text-brand-slate">{basisLabel(r)}</TD>
                <TD className="text-right text-xs font-semibold">{Number(r.percent) || 0}%</TD>
                <TD className="text-xs text-brand-slate">
                  {r.startsOn || "—"} → {r.endsOn || "ongoing"}
                </TD>
                <TD className="text-right whitespace-nowrap">
                  <button
                    title="Edit rule"
                    onClick={() => { setEditing(r); setFormOpen(true); }}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate hover:text-brand-ink"
                  >
                    <Edit2 size={12} />
                  </button>
                  <button
                    title="Delete rule"
                    onClick={() => dashboard.deletePartnerRule(r.id)}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate hover:text-red-600"
                  >
                    <Trash2 size={12} />
                  </button>
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}

      {formOpen && (
        <RuleForm
          open={true}
          initial={editing}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSubmit={(data) => {
            if (editing) dashboard.updatePartnerRule(editing.id, data);
            else dashboard.addPartnerRule({ ...data, partnerId: member.id });
            setFormOpen(false);
            setEditing(null);
          }}
          offers={offers}
          departments={departments}
        />
      )}
    </div>
  );
}

// Modal form for adding/editing a single partner rule.
function RuleForm({ open, onClose, onSubmit, initial, offers, departments }) {
  const [form, setForm] = useState(() => initial || {
    basis: "revenue",
    scope: "department",
    scopeId: departments[0] || "",
    percent: "",
    startsOn: toISO(today()),
    endsOn: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (form.scope !== "agency" && !form.scopeId) errs.scopeId = "Pick a target";
    const pct = Number(form.percent);
    if (!(pct > 0 && pct <= 100)) errs.percent = "Must be 0–100";
    if (!form.startsOn) errs.startsOn = "Required";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit({
      basis: form.basis,
      scope: form.scope,
      scopeId: form.scope === "agency" ? null : form.scopeId,
      percent: pct,
      startsOn: form.startsOn,
      endsOn: form.endsOn || null,
      notes: form.notes || null,
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit partner rule" : "Add partner rule"} size="sm">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Cut against" value={form.basis} onChange={(e) => update("basis", e.target.value)}>
            <option value="revenue">% of revenue</option>
            <option value="net_profit">% of net profit</option>
          </Select>
          <Input
            label="Percent *"
            type="number"
            step="0.1"
            value={form.percent}
            onChange={(e) => update("percent", e.target.value)}
            error={errors.percent}
            placeholder="e.g. 50"
          />
        </div>
        <Select
          label="Scope"
          value={form.scope}
          onChange={(e) => {
            const next = e.target.value;
            // Reset scopeId when scope changes — different scope, different target.
            update("scope", next);
            if (next === "agency") update("scopeId", "");
            else if (next === "department") update("scopeId", departments[0] || "");
            else update("scopeId", offers[0]?.id || "");
          }}
        >
          <option value="department">By department</option>
          <option value="offer">By offer</option>
          <option value="agency">Agency-wide</option>
        </Select>

        {form.scope === "department" && (
          <Select label="Department *" value={form.scopeId} onChange={(e) => update("scopeId", e.target.value)} error={errors.scopeId}>
            <option value="">Select department</option>
            {departments.map((d) => <option key={d} value={d}>{d}</option>)}
          </Select>
        )}
        {form.scope === "offer" && (
          <Select label="Offer *" value={form.scopeId} onChange={(e) => update("scopeId", e.target.value)} error={errors.scopeId}>
            <option value="">Select offer</option>
            {offers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </Select>
        )}
        {form.scope === "agency" && (
          <div className="text-[11px] text-brand-slate px-3 py-2 rounded-md bg-gray-50 dark:bg-[var(--bg-soft)]">
            Applies to the agency-wide total. Used for rules like "25% of overall net profit".
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input label="Starts on *" type="date" value={form.startsOn} onChange={(e) => update("startsOn", e.target.value)} error={errors.startsOn} />
          <Input label="Ends on (optional)" type="date" value={form.endsOn || ""} onChange={(e) => update("endsOn", e.target.value)} />
        </div>

        <Input label="Notes" value={form.notes || ""} onChange={(e) => update("notes", e.target.value)} />

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit">{initial ? "Save" : "Add rule"}</Button>
        </div>
      </form>
    </Modal>
  );
}
